"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.openAiProvider = void 0;
/* eslint-disable no-nested-ternary */
const __1 = require("../..");
const json_1 = require("../../../core/utils/json");
const media_1 = require("../../../core/normalize/message-parts/media");
const reasoning_1 = require("../../../core/normalize/message-parts/reasoning");
const tool_calls_1 = require("../../../core/normalize/message-parts/tool-calls");
const tool_results_1 = require("../../../core/normalize/message-parts/tool-results");
const tool_definitions_1 = require("../../../core/normalize/tool-definitions");
/**
 * OpenAI convention: Chat Completions and Responses API shapes. This module
 * owns OpenAI's typed block vocabulary, its `choices`/`output` response
 * envelopes, and the `refusal`/`audio`/reasoning-item sibling fields.
 */
// OpenAI Chat Completions `finish_reason` vocabulary -> the canonical
// FinishReason set.
const OPENAI_FINISH_REASON_TYPE_BY_RAW = {
    stop: "stop",
    length: "length",
    tool_calls: "tool-calls",
    function_call: "tool-calls",
    content_filter: "content-filter",
};
/**
 * OpenAI Responses built-in (provider-executed) tool items. They carry no
 * `name`/`arguments`; the item type is the tool. Kind-specific payloads
 * (action, queries, code, results, ...) travel in `input` unsplit — the API
 * reports request and result on the same item.
 */
const RESPONSES_BUILT_IN_TOOL_ITEM_TYPES = [
    "web_search_call",
    "file_search_call",
    "code_interpreter_call",
    "computer_call",
    "image_generation_call",
    "local_shell_call",
    "shell_call",
    "apply_patch_call",
    "tool_search_call",
];
function normalizeBuiltInToolItem(value) {
    const type = typeof value.type === "string" ? value.type : undefined;
    if (!type)
        return null;
    if (type === "mcp_call") {
        // MCP calls are real named calls that happen to be provider-executed;
        // server_label/output/error ride in providerMetadata.
        return (0, tool_calls_1.providerExecutedToolCall)((0, tool_calls_1.toolCallPart)({
            toolCallId: value.call_id ?? value.id,
            toolName: value.name,
            input: value.arguments,
            toolType: type,
        }), (0, json_1.omitKeys)(value, ["id", "call_id", "type", "name", "arguments", "status"]));
    }
    const status = (0, json_1.optionalString)(value.status);
    return (0, json_1.compact)({
        type: "tool-call",
        toolCallId: (0, json_1.nullableString)(value.call_id ?? value.id),
        toolName: type.replace(/_call$/, ""),
        input: (0, json_1.toJsonValue)((0, json_1.omitKeys)(value, ["id", "call_id", "type", "status"])),
        toolType: type,
        providerExecuted: true,
        providerMetadata: status ? { status } : undefined,
    });
}
// Chat Completions tool_calls entries ({ id, index?, function: { name,
// arguments } }) and Responses/legacy flat items ({ call_id | id, name,
// arguments }) share these two type names.
const normalizeOpenAiFunctionCall = (value) => {
    const functionCall = (0, json_1.asRecord)(value.function);
    return (0, __1.claimed)((0, tool_calls_1.toolCallPart)({
        toolCallId: value.call_id ?? value.id,
        toolName: value.name ?? functionCall?.name,
        input: value.arguments ?? functionCall?.arguments,
        toolType: (0, json_1.optionalString)(value.type) ?? (functionCall ? "function" : undefined),
    }));
};
const normalizeOpenAiCustomToolCall = (value) => {
    // OpenAI custom tool call: { id, type: "custom", custom: { name, input } }.
    const custom = (0, json_1.asRecord)(value.custom);
    if (!custom || !(0, json_1.optionalString)(custom.name) || !("input" in custom)) {
        return __1.unmatched;
    }
    return (0, __1.claimed)((0, tool_calls_1.toolCallPart)({
        toolCallId: value.id,
        toolName: custom.name,
        input: custom.input,
        toolType: "custom",
    }));
};
const normalizeOpenAiImageUrl = (value) => {
    const image = (0, json_1.asRecord)(value.image_url);
    const url = (0, json_1.optionalString)(image?.url);
    if (!url)
        return __1.unmatched;
    const detail = (0, json_1.optionalString)(image?.detail);
    const part = (0, media_1.filePartFromUrl)(url, {
        fallbackMediaType: "image/*",
        extras: detail ? { detail } : undefined,
    });
    return (0, __1.claimed)(part);
};
const normalizeOpenAiInputImage = (value) => {
    // OpenAI Responses image: flat fields instead of the chat wrapper.
    const detail = (0, json_1.optionalString)(value.detail);
    const extras = detail ? { detail } : undefined;
    const url = (0, json_1.optionalString)(value.image_url);
    if (url) {
        return (0, __1.claimed)((0, media_1.filePartFromUrl)(url, { fallbackMediaType: "image/*", extras }));
    }
    const fileId = (0, json_1.optionalString)(value.file_id);
    if (!fileId)
        return __1.unmatched;
    return (0, __1.claimed)((0, json_1.compact)({
        type: "file",
        mediaType: "image/*",
        content: { kind: "reference", id: fileId },
        providerMetadata: extras ? (0, json_1.toProviderMetadata)(extras) : undefined,
    }));
};
const normalizeOpenAiInputAudio = (value) => {
    const audio = (0, json_1.asRecord)(value.input_audio);
    const data = (0, json_1.optionalString)(audio?.data);
    if (!data)
        return __1.unmatched;
    const reference = (0, media_1.parseMediaReference)(data);
    if (reference)
        return (0, __1.claimed)((0, media_1.filePartFromMediaReference)(reference));
    const format = (0, json_1.optionalString)(audio?.format);
    return (0, __1.claimed)({
        type: "file",
        mediaType: format ? `audio/${format}` : "audio/*",
        content: { kind: "base64", data },
    });
};
/**
 * OpenAI file fields (`file` content parts and Responses `input_file` items
 * share them): base64 `file_data` (possibly a media token), a `file_url`, or
 * an opaque `file_id` reference, plus an optional `filename`.
 */
function filePartFromFileFields(fields) {
    const filename = (0, json_1.optionalString)(fields.filename);
    const fileData = (0, json_1.optionalString)(fields.file_data);
    const reference = (0, media_1.parseMediaReference)(fileData);
    if (reference) {
        return (0, json_1.compact)({ ...(0, media_1.filePartFromMediaReference)(reference), filename });
    }
    const fileUrl = (0, json_1.optionalString)(fields.file_url);
    const fileId = (0, json_1.optionalString)(fields.file_id);
    const content = fileData
        ? { kind: "base64", data: fileData }
        : fileUrl
            ? { kind: "url", url: fileUrl }
            : fileId
                ? { kind: "reference", id: fileId }
                : undefined;
    if (!content)
        return null;
    return (0, json_1.compact)({ type: "file", filename, content });
}
const normalizeOpenAiFile = (value) => {
    // OpenAI chat `file` content parts wrap the fields under `file`; flat
    // `data`/`url` payloads on the same type name belong to the AI SDK
    // dialect and fall through.
    const file = (0, json_1.asRecord)(value.file);
    if (!file)
        return __1.unmatched;
    const part = filePartFromFileFields(file);
    return part ? (0, __1.claimed)(part) : __1.unmatched;
};
const normalizeOpenAiInputFile = (value) => {
    // OpenAI Responses file: same fields as the chat `file` wrapper, flat.
    const part = filePartFromFileFields(value);
    return part ? (0, __1.claimed)(part) : __1.unmatched;
};
const normalizeOpenAiRefusal = (value) => {
    // Refusal text stays part of the conversation stream.
    const refusal = (0, json_1.optionalString)(value.refusal);
    return refusal
        ? (0, __1.claimed)({ type: "text", refusal: true, text: refusal })
        : __1.unmatched;
};
const normalizeOpenAiMcpCall = (value) => {
    const part = normalizeBuiltInToolItem(value);
    return part ? (0, __1.claimed)(part) : __1.unmatched;
};
const normalizeOpenAiBuiltInCall = (value) => (0, __1.claimed)(normalizeBuiltInToolItem(value));
// OpenAI Responses output items: { type: "*_output", call_id, output }.
const normalizeOpenAiToolResult = (value) => {
    const raw = (0, json_1.parseIfString)(value.output ?? null);
    return (0, __1.claimed)((0, json_1.compact)({
        type: "tool-result",
        toolCallId: (0, json_1.nullableString)(value.call_id ?? value.id),
        output: typeof raw === "string"
            ? { type: "text", value: raw }
            : { type: "json", value: (0, json_1.toJsonValue)(raw) },
    }));
};
// OpenAI Responses custom (free-form input) tool call:
// { type: "custom_tool_call", call_id, name, input }.
const normalizeOpenAiResponsesCustomToolCall = (value) => (0, __1.claimed)((0, tool_calls_1.toolCallPart)({
    toolCallId: value.call_id ?? value.id,
    toolName: value.name,
    input: value.input ?? value.arguments,
    toolType: "custom",
}));
// OpenAI Responses reasoning content/summary entries: { type, text }.
const normalizeOpenAiReasoningText = (value) => (0, __1.claimed)((0, reasoning_1.reasoningPart)(value.text));
function isReasoningTextArray(value, expectedType) {
    if (!Array.isArray(value))
        return false;
    return value.every((entry) => {
        const part = (0, json_1.asRecord)(entry);
        return part?.type === expectedType && typeof part.text === "string";
    });
}
const normalizeOpenAiReasoning = (value, context) => {
    if (!isReasoningTextArray(value.summary, "summary_text")) {
        return __1.unmatched;
    }
    const hasInvalidContent = value.content !== undefined &&
        !isReasoningTextArray(value.content, "reasoning_text");
    if (hasInvalidContent) {
        return __1.unmatched;
    }
    const encryptedContent = value.encrypted_content;
    const isValidEncryptedContent = encryptedContent === undefined ||
        encryptedContent === null ||
        typeof encryptedContent === "string";
    if (!isValidEncryptedContent) {
        return __1.unmatched;
    }
    // Text-based reasoning belongs to the shared handler.
    if (value.text !== undefined) {
        return __1.unmatched;
    }
    return (0, __1.claimed)(openAiReasoningParts(value, [], context));
};
const OPENAI_PART_HANDLERS = {
    function: normalizeOpenAiFunctionCall,
    function_call: normalizeOpenAiFunctionCall,
    custom_tool_call: normalizeOpenAiResponsesCustomToolCall,
    custom: normalizeOpenAiCustomToolCall,
    image_url: normalizeOpenAiImageUrl,
    input_image: normalizeOpenAiInputImage,
    input_audio: normalizeOpenAiInputAudio,
    file: normalizeOpenAiFile,
    input_file: normalizeOpenAiInputFile,
    refusal: normalizeOpenAiRefusal,
    reasoning_text: normalizeOpenAiReasoningText,
    reasoning: normalizeOpenAiReasoning,
    summary_text: normalizeOpenAiReasoningText,
    mcp_call: normalizeOpenAiMcpCall,
    ...Object.fromEntries(RESPONSES_BUILT_IN_TOOL_ITEM_TYPES.map((type) => [
        type,
        normalizeOpenAiBuiltInCall,
    ])),
    function_call_output: normalizeOpenAiToolResult,
    custom_tool_call_output: normalizeOpenAiToolResult,
    computer_call_output: normalizeOpenAiToolResult,
    local_shell_call_output: normalizeOpenAiToolResult,
};
/**
 * OpenAI audio output (`message.audio`): `{ id, data, transcript, expires_at }`
 * on response messages, `{ id }` as the request-side reference. The playable
 * payload becomes the file part; transcript and the remaining fields ride
 * along in providerMetadata so the stream stays renderable media-first.
 */
function normalizeAudioOutput(audio) {
    if (!audio)
        return null;
    const { data, ...extras } = audio;
    const payload = (0, json_1.optionalString)(data);
    const reference = (0, media_1.parseMediaReference)(payload);
    if (reference)
        return (0, media_1.filePartFromMediaReference)(reference, extras);
    if (payload) {
        return (0, json_1.compact)({
            type: "file",
            mediaType: "audio/*",
            content: { kind: "base64", data: payload },
            providerMetadata: (0, json_1.toProviderMetadata)(extras),
        });
    }
    const id = (0, json_1.optionalString)(audio.id);
    if (!id)
        return null;
    return (0, json_1.compact)({
        type: "file",
        mediaType: "audio/*",
        content: { kind: "reference", id },
        providerMetadata: (0, json_1.toProviderMetadata)((0, json_1.omitKeys)(extras, ["id"])),
    });
}
/**
 * OpenAI message-sibling fields: `refusal`/`audio`, and Responses reasoning
 * items (content[] is collected via the regular parts path; `summary` is a
 * sibling stream collected either way, and the replayable `encrypted_content`
 * blob becomes its own stream element after the visible parts).
 */
function openAiCollectSiblingParts(value, baseParts, context) {
    const parts = [];
    const refusal = (0, json_1.optionalString)(value.refusal);
    if (refusal)
        parts.push({ type: "text", refusal: true, text: refusal });
    const audioPart = normalizeAudioOutput((0, json_1.asRecord)(value.audio));
    if (audioPart)
        parts.push(audioPart);
    if (value.type === "reasoning") {
        parts.push(...openAiReasoningParts(value, baseParts, context));
    }
    return parts.length > 0
        ? [{ sourceKey: "openai.siblings", slot: "after-tool-calls", parts }]
        : [];
}
function openAiReasoningParts(value, baseParts, context) {
    const parts = [];
    const reasoningValues = (baseParts.length === 0 ? [value.summary, value.content] : [value.summary])
        .flatMap((entry) => (Array.isArray(entry) ? entry : [entry]))
        .filter((entry) => entry !== undefined && entry !== null);
    parts.push(...context.normalizePartList(reasoningValues));
    const encryptedContent = (0, json_1.optionalString)(value.encrypted_content);
    if (encryptedContent) {
        parts.push({
            type: "reasoning",
            content: { kind: "encrypted", data: encryptedContent },
        });
    }
    return parts;
}
function isResponsesRequest(root) {
    if (typeof root.input === "string" && typeof root.model === "string") {
        return true;
    }
    const responseInput = (0, json_1.parseArray)(root.input);
    return Boolean(responseInput?.length &&
        responseInput.every((item) => {
            const record = (0, json_1.asRecord)(item);
            return (record &&
                (typeof record.role === "string" || typeof record.type === "string"));
        }));
}
/**
 * OpenAI response envelopes: Chat Completions `choices[]` (finish reason
 * per choice) and Responses' `output[]` item array.
 */
function openAiMessages(root, kind) {
    // Responses API uses `input` instead of Chat Completions' `messages`:
    if (kind === "input") {
        if (!isResponsesRequest(root))
            return [];
        const input = (0, json_1.recordKeyAsParsed)(root, "input");
        return typeof input === "string" && typeof root.model === "string"
            ? [{ kind: "single", value: input, fallbackRole: "user" }]
            : [
                {
                    kind: "sequence",
                    values: (0, json_1.parseArray)(input) ?? [],
                    fallbackRole: "user",
                },
            ];
    }
    if (kind !== "output")
        return [];
    const choices = (0, json_1.parseArray)(root.choices);
    if (choices) {
        const sources = [];
        for (const choice of choices) {
            const choiceRecord = (0, json_1.asRecord)(choice);
            sources.push({
                kind: "single",
                value: choiceRecord?.message,
                fallbackRole: "assistant",
                // Chat Completions reports the finish reason on the choice, not the
                // message.
                finishReasonCarrier: choiceRecord,
            });
        }
        return sources;
    }
    const responseOutput = (0, json_1.parseArray)(root.output);
    if (responseOutput &&
        responseOutput.some((item) => {
            const record = (0, json_1.asRecord)(item);
            return Boolean(record?.type &&
                (String(record.type).includes("call") ||
                    String(record.type).endsWith("_output") ||
                    record.type === "message" ||
                    record.type === "reasoning"));
        })) {
        return [
            {
                kind: "sequence",
                values: responseOutput,
                fallbackRole: "assistant",
            },
        ];
    }
    return [];
}
// OpenAI carries citations as `annotations` — per part on Responses
// output_text, message-level on Chat Completions.
const OPENAI_CITATION_KEYS = new Set(["annotations"]);
// Chat Completions carries assistant calls in a `tool_calls` sibling array.
const OPENAI_MESSAGE_LIKE_KEYS = new Set(["tool_calls"]);
/**
 * OpenAI tool declarations: function tools wrap their fields under
 * `function`; custom (free-form input) tools wrap under `custom` or declare
 * flat with `format` as the input schema (Responses).
 */
const tryNormalizeOpenAiToolDefinition = (value) => {
    const functionDefinition = (0, json_1.asRecord)(value.function);
    if (functionDefinition) {
        const definition = (0, tool_definitions_1.toolDefinition)({
            name: functionDefinition.name ?? value.name,
            description: functionDefinition.description,
            inputSchema: functionDefinition.parameters,
            type: value.type,
            providerMetadata: (0, tool_definitions_1.toolDefinitionProviderMetadata)(value, functionDefinition),
        });
        return definition ? (0, __1.claimed)(definition) : __1.unmatched;
    }
    const customDefinition = (0, json_1.asRecord)(value.custom);
    if (customDefinition) {
        const definition = (0, tool_definitions_1.toolDefinition)({
            name: customDefinition.name ?? value.name,
            description: customDefinition.description,
            inputSchema: customDefinition.format,
            type: value.type,
            providerMetadata: (0, tool_definitions_1.toolDefinitionProviderMetadata)(value, customDefinition),
        });
        return definition ? (0, __1.claimed)(definition) : __1.unmatched;
    }
    if (value.format !== undefined) {
        const definition = (0, tool_definitions_1.toolDefinition)({
            name: value.name,
            description: value.description,
            inputSchema: value.format,
            type: value.type,
            providerMetadata: (0, tool_definitions_1.toolDefinitionProviderMetadata)(value, value),
        });
        return definition ? (0, __1.claimed)(definition) : __1.unmatched;
    }
    return __1.unmatched;
};
/**
 * Deprecated function-calling protocol: `{role: "function", name, content}`
 * is a tool result carrying the function name instead of a tool_call_id.
 * Claimed as a whole message so `name` becomes the tool name rather than a
 * participant name.
 */
const tryUnwrapLegacyFunctionMessage = (value, _fallbackRole, ctx) => {
    const content = (0, json_1.parseIfString)(value.content ?? null);
    let output = typeof content === "string"
        ? { type: "text", value: content }
        : { type: "json", value: (0, json_1.toJsonValue)(content) };
    if (
    // LangChain dict serializations carry `type: "tool"` instead of a role
    // key; the same tool-result shape (tool_call_id, artifact, status).
    (value.role === "tool" || value.type === "tool") &&
        (typeof value.tool_call_id === "string" ||
            typeof value.tool_call_id === "number")) {
        if (Array.isArray(value.content)) {
            output = (0, tool_results_1.toolResultOutput)(ctx.normalizePartList(value.content), content);
        }
        return (0, __1.claimed)({
            ...((0, json_1.optionalString)(value.id) ? { id: String(value.id) } : {}),
            role: "tool",
            parts: [
                (0, json_1.compact)({
                    type: "tool-result",
                    toolCallId: (0, json_1.nullableString)(value.tool_call_id),
                    // A `name` on a tool message is the tool's name, never a
                    // participant name.
                    toolName: (0, json_1.optionalString)(value.name),
                    output,
                    isError: value.status === "error" ? true : undefined,
                    providerMetadata: value.artifact !== undefined && value.artifact !== null
                        ? (0, json_1.toProviderMetadata)({ artifact: value.artifact })
                        : undefined,
                }),
            ],
            source: ctx.source,
        });
    }
    if (typeof value.role !== "string" ||
        value.role.toLowerCase() !== "function") {
        return __1.unmatched;
    }
    return (0, __1.claimed)({
        ...((0, json_1.optionalString)(value.id) ? { id: String(value.id) } : {}),
        role: "tool",
        parts: [
            (0, json_1.compact)({
                type: "tool-result",
                toolCallId: (0, json_1.nullableString)(value.tool_call_id),
                toolName: (0, json_1.optionalString)(value.name),
                output,
            }),
        ],
        source: ctx.source,
    });
};
exports.openAiProvider = {
    name: "openai",
    finishReasonTypeByRaw: OPENAI_FINISH_REASON_TYPE_BY_RAW,
    tryUnwrapMessage: tryUnwrapLegacyFunctionMessage,
    tryNormalizeToolDefinition: tryNormalizeOpenAiToolDefinition,
    // Deprecated function-calling protocol: function messages are tool results.
    // `developer` is OpenAI's replacement name for `system` (o1+); the API
    // treats them as aliases, so the canonical vocabulary keeps only `system`.
    roleByRawRole: { function: "tool", developer: "system" },
    // Responses reasoning items carry no role but are model output even when
    // replayed on the input side.
    roleByMessageType: { reasoning: "assistant" },
    messageLikeKeys: OPENAI_MESSAGE_LIKE_KEYS,
    citationKeys: OPENAI_CITATION_KEYS,
    typedParts: OPENAI_PART_HANDLERS,
    collectSiblingParts: openAiCollectSiblingParts,
    claimMessages: openAiMessages,
    getSystemMessage: (root, kind) => kind === "input" &&
        typeof root.instructions === "string" &&
        isResponsesRequest(root)
        ? {
            kind: "single",
            value: (0, json_1.recordKeyAsParsed)(root, "instructions"),
            fallbackRole: "user",
            roleOverride: "system",
        }
        : undefined,
};
